import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTwilioWS } from '@hooks/twilioWS';
import useMyRole from '@hooks/useMyRole';
import * as actions from '@actions';
import { useDispatch } from 'react-redux';
import { useQueryState, parseAsInteger } from '@lib/queryState';
import { Appointment } from 'vl-common/src/schemas/shared';
import { useVideoCallSignallingType } from '@hooks/useVideoCallSignallingType';
import { useAppointmentRoomDT } from '@hooks/useTestPatientRoom';

/**
 * WebSocket implementation of appointment delays hook.
 * Custom hook to be used by the patient (will not yield updates on the clinician's side) which will notify
 * when the clinician delays the start of the appointment or cancels the appointment (as controlled by
 * useDelayAppointment()).
 *
 * @param appointment
 * @returns {delayInMinutes}
 */
function useAppointmentDelaysWS(appointment: Appointment): { delayInMinutes: number; delay: () => void } {
  const { appointment_id, appointment_delay_mins } = appointment;
  const myRole = useMyRole();
  const dispatch = useDispatch();
  const [queryDelay, setQueryDelay] = useQueryState(`delay_for_${appointment_id}`, parseAsInteger);
  const [delayInMinutes, setDelayInMinutes] = useState(Math.max(appointment_delay_mins, queryDelay || 0));

  useEffect(() => {
    setDelayInMinutes(appointment_delay_mins);
  }, [appointment_id]);

  const onControlMessage = useCallback(
    (msg: Record<string, any>) => {
      if ('delay' in msg) {
        setDelayInMinutes(msg.delay);
        setQueryDelay(msg.delay);
      }
    },
    [setQueryDelay]
  );
  const { sendControlMessage } = useTwilioWS(
    appointment,
    useMemo(() => ({ onControlMessage }), [onControlMessage])
  );

  const nullUpdater = useCallback(() => {}, []);
  const updater = useCallback(() => {
    const p = dispatch(actions.delayAppointment(appointment_id));

    return p.then(({ delay_minutes }) => {
      setDelayInMinutes((current_delay) => {
        if (delay_minutes) {
          sendControlMessage({ delay: delay_minutes });
        }

        return delay_minutes || current_delay;
      });
    });
  }, [appointment_id, dispatch, sendControlMessage]);

  return { delayInMinutes, delay: myRole === 'clinician' ? updater : nullUpdater };
}

/**
 * DataTrack implementation of appointment delays hook.
 * Same functionality as WS version but receives delay via Twilio DataTrack instead of WebSocket.
 *
 * @param appointment
 * @returns {delayInMinutes}
 */
function useAppointmentDelaysDT(appointment: Appointment): { delayInMinutes: number; delay: () => void } {
  const { appointment_id, appointment_delay_mins } = appointment;
  const myRole = useMyRole();
  const dispatch = useDispatch();
  const [queryDelay, setQueryDelay] = useQueryState(`delay_for_${appointment_id}`, parseAsInteger);
  const [delayInMinutes, setDelayInMinutes] = useState(Math.max(appointment_delay_mins, queryDelay || 0));
  const { signalingManager } = useAppointmentRoomDT(appointment);

  useEffect(() => {
    setDelayInMinutes(appointment_delay_mins);
  }, [appointment_id]);

  // Subscribe to delay updates via DataTrack
  useEffect(() => {
    if (signalingManager) {
      const unsubscribe = signalingManager.subscribeToDelay((minutes: number) => {
        setDelayInMinutes(minutes);
        setQueryDelay(minutes);
      });
      return unsubscribe;
    }
  }, [signalingManager, setQueryDelay]);

  const nullUpdater = useCallback(() => {}, []);
  const updater = useCallback(() => {
    const p = dispatch(actions.delayAppointment(appointment_id));

    return p.then(({ delay_minutes }) => {
      if (delay_minutes) {
        setDelayInMinutes(delay_minutes);
        if (signalingManager) {
          signalingManager.delayAppointment(delay_minutes);
        }
      }
    });
  }, [appointment_id, dispatch, signalingManager]);

  return { delayInMinutes, delay: myRole === 'clinician' ? updater : nullUpdater };
}

/**
 * Switcher hook that chooses between WS and DT implementations.
 * Returns the appropriate delays hook based on appointment's signalling type.
 * NOTE: appointment must be stable (not change during component lifecycle).
 */
function useAppointmentDelays(appointment: Appointment): { delayInMinutes: number; delay: () => void } {
  const signallingType = useVideoCallSignallingType(appointment);

  const useAppointmentDelaysImpl = signallingType === 'ws' ? useAppointmentDelaysWS : useAppointmentDelaysDT;

  return useAppointmentDelaysImpl(appointment);
}

export default useAppointmentDelays;
